import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Breadcrumb } from '@app/models/breadcrumb.model';
import { VincereTokenServerSide } from '@app/models/integrations/vincere/vincere-token-server-side.model';
import { VincereToken } from '@app/models/integrations/vincere/vincere-token.model';
import { VincereAPIService } from '@app/shared/api/vincere.api.service';
import { BreadcrumbService } from '@app/shared/breadcrumbs/breadcrumbs.service';
import { SwalUtils } from '@app/shared/utils/swal.utils';
import { environment } from 'environments/environment';
import { finalize } from 'rxjs/operators';


enum WorkflowState {
  INITIAL,
  SETUP,
  SYNCING,
  COMPLETE,
  UPDATE_API_KEY,
  UPDATE_REFRESH_TOKEN
}

@Component({
  selector: 'app-integration-vincere',
  templateUrl: './integration-vincere.component.html',
  styleUrls: ['./integration-vincere.component.scss']
})
export class IntegrationVincereComponent implements OnInit, OnDestroy {
  public readonly eWorkflowState = WorkflowState;

  breadcrumb: Breadcrumb;
  state: WorkflowState;
  vincereError: string | null;

  setupApiKeyForm: FormGroup;
  token: VincereToken | null;
  setupApiKeyInvalid: string | null; // tracks if the key was flagged as invalid and hasn't been changed in the form since
  setupClientIdInvalid: string | null;
  apiKeyCopied: boolean;
  loadingSetup: boolean;
  setupSubmitted: boolean;
  setupCompleting: boolean;
  setupConnecting: boolean;

  get controlKey(): FormControl {
    return this.setupApiKeyForm.controls.key as FormControl;
  }

  get controlClientId(): FormControl {
    return this.setupApiKeyForm.controls.clientId as FormControl;
  }

  get controlTenantId(): FormControl {
    return this.setupApiKeyForm.controls.tenantId as FormControl;
  }

  constructor(
        private breadcrumbService: BreadcrumbService,
        public route: ActivatedRoute,
        private vincereAPIService: VincereAPIService,
        private formBuilder: FormBuilder,
        private swalUtils: SwalUtils
  ) {
    this.token = null;
    this.setupApiKeyInvalid = null;
    this.setupClientIdInvalid = null;

    this.apiKeyCopied = false;
    this.loadingSetup = false;
    this.setupSubmitted = false;
    this.setupCompleting = false;
    this.setupConnecting = false;

    this.state = WorkflowState.INITIAL;

    this.breadcrumb = this.breadcrumbService.init(this.route);

    this.setupApiKeyForm = this.initForm();

    this.vincereError = localStorage.getItem('vincere_error');
  }

  initForm(): FormGroup {
    const formGroup = this.formBuilder.group({
      key: new FormControl('', [Validators.required]),
      clientId: new FormControl('', [Validators.required]),
      tenantId: new FormControl('', [Validators.required, Validators.maxLength(2000)])
    });

    formGroup.controls.key.valueChanges.subscribe(() => {
      this.setupApiKeyInvalid = null; // remove flag if user has changed value in textbox
    });

    return formGroup;
  }

  ngOnInit(): void {
    this.checkForVincereError();
    this.vincereAPIService.getToken().subscribe(token => {
      this.token = token;
      if (this.token) {
        this.controlTenantId.setValue(this.token.tenantId);
        this.controlKey.setValue(this.token.apiKey);
        this.controlClientId.setValue(this.token.clientId);
        if (!this.token.connected) {
          this.state = WorkflowState.UPDATE_REFRESH_TOKEN;
        } else {
          this.state = WorkflowState.COMPLETE;
        }

      }
    });
  }


  checkForVincereError() {
    if (this.vincereError) {
      const timestamp = +this.vincereError.split(':')[0];
      const message = this.vincereError.split(':')[1];
      // Check if error is 30 seconds old or less
      if (Date.now() - timestamp < 30 * 1000) {
        this.swalUtils.displayErrorSwal({
          title: 'Error',
          text: message,
          confirmButtonColor: '#2DA1B3',
        });
      }
      localStorage.removeItem('vincere_error');
    }
  }

  ngOnDestroy() {
    this.breadcrumbService.remove(this.breadcrumb);
  }

  timeoutCheckmarkIconApiKey(): void {
    setTimeout(() => {
      this.apiKeyCopied = false;
    }, 1000);
  }

  startSetup(): void {
    this.loadingSetup = true;
    this.state = WorkflowState.SETUP;
  }

  saveApiKey(): void {
    this.setupSubmitted = true;

    if (this.setupApiKeyForm.invalid) {
      return;
    }

    this.setupCompleting = true;
    const vincereToken = {
      tenantId: this.controlTenantId.value,
      apiKey: this.controlKey.value,
      clientId: this.controlClientId.value
    } as VincereTokenServerSide;

    this.vincereAPIService.addApiKey(vincereToken)
      .pipe(finalize(() => this.setupCompleting = false)).subscribe(token => {
        this.token = token;
        this.state = WorkflowState.UPDATE_REFRESH_TOKEN;
      });
  }

  startUpdate(): void {
    this.state = WorkflowState.UPDATE_API_KEY;
    // this.controlTenantId.setValue('');
    // this.controlKey.setValue('');
    // this.controlClientId.setValue('');
    this.setupSubmitted = false;
    this.setupApiKeyInvalid = null;
    this.setupCompleting = false;
  }

  completeUpdate(): void {
    this.state = WorkflowState.UPDATE_REFRESH_TOKEN;
  }

  cancelUpdate(): void {
    this.state = this.token!.connected ? WorkflowState.COMPLETE : WorkflowState.UPDATE_REFRESH_TOKEN;
  }

  connectToVincere() {
    const redirectUri = environment.vincere.redirectUri;
    document.location.href = `https://id.vincere.io/oauth2/authorize?response_type=code&client_id=${this.token?.clientId}&redirect_uri=${redirectUri}&state=STATE`;
  }

}
